import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Sends postMessage events up to a parent window when the quiz is embedded
 * in an iframe (e.g. on a Shopify page). The parent listens for these
 * events to:
 *   - resize the iframe to match content height (so no inner scrollbar)
 *   - scroll the parent page to the top of the iframe on route change
 *     (so the customer sees the next question's heading, not the bottom
 *     of the previous screen)
 *
 * No-ops when not inside an iframe (window === window.parent).
 *
 * Message protocol:
 *   { type: 'fitquiz:height',  height: number }   // current content height
 *   { type: 'fitquiz:route',   path:   string }   // pathname after navigation
 */
export function IframeBridge() {
  const location = useLocation()
  const inIframe = typeof window !== 'undefined' && window.parent !== window
  const mounted = useRef(false)

  // Mark the body so CSS can suppress 100vh-based min-heights that would
  // create a resize feedback loop (content grows → iframe grows → 100vh grows → repeat).
  useEffect(() => {
    if (!inIframe) return
    document.body.classList.add('fq-embedded')
    return () => document.body.classList.remove('fq-embedded')
  }, [inIframe])

  // Height: observe the body and post on every change, debounced so rapid
  // firing during Framer Motion animations doesn't cause the parent page to
  // jump on every animation frame.
  useEffect(() => {
    if (!inIframe) return

    let timer: ReturnType<typeof setTimeout>

    const post = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        try {
          window.parent.postMessage({ type: 'fitquiz:height', height: document.body.scrollHeight }, '*')
        } catch { /* cross-origin restrictions can throw - best effort */ }
      }, 50)
    }

    post()

    const observer = new ResizeObserver(post)
    observer.observe(document.body)

    // When the parent page is restored from bfcache (user navigates away
    // then hits back), our last height post is stale - the parent's iframe
    // may have collapsed or never re-rendered. Re-post on pageshow so the
    // parent resizes us correctly again.
    const onPageShow = () => post()
    window.addEventListener('pageshow', onPageShow)
    const onVisible = () => { if (document.visibilityState === 'visible') post() }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      observer.disconnect()
      clearTimeout(timer)
      window.removeEventListener('pageshow', onPageShow)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [inIframe])

  // bfcache restore: when the parent page is restored from the back-forward
  // cache (user clicks a product link from /result, then hits Back to return
  // to the quiz page), the iframe sometimes comes back without its scripts
  // re-running - leaving a blank or stale-state quiz. A hard reload on
  // bfcache hit guarantees a fresh quiz every time the user re-enters.
  useEffect(() => {
    const onPageShowReload = (e: PageTransitionEvent) => {
      if (e.persisted) window.location.reload()
    }
    window.addEventListener('pageshow', onPageShowReload)
    return () => window.removeEventListener('pageshow', onPageShowReload)
  }, [])

  // Route change: ask parent to scroll the iframe back into view.
  // Skip on initial mount — only scroll on actual navigation between screens.
  useEffect(() => {
    if (!inIframe) return
    if (!mounted.current) { mounted.current = true; return }
    try {
      window.parent.postMessage({ type: 'fitquiz:route', path: location.pathname }, '*')
    } catch { /* noop */ }
  }, [inIframe, location.pathname])

  return null
}
